import React, { useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import {
  Container,
  CountdownBox,
  CountdownText,
  DurationText,
  SectionTitle,
  StatusChip,
  StyledLinearProgress,
  TimeLabel,
  TimeValue,
  WorkerId,
  WorkerName
} from './styles';
import ActionModal from '../../../../../components/ActionModal';
import VerifyShift from './components/VerifyShift';
import PerformanceFeedback from './components/PerformanceFeedback';
import WarningIcon from '../../../../../assets/main/warning.svg';
import SecureAvatar from 'components/common/SecureAvatar';

interface WorkerAssignedProps {
  name: string;
  id: number | string;
  avatarUrl: string;
  status: string;
  clockIn: string | Date | null;
  clockOut?: string | Date | null;
  shiftStart: string | Date;
  shiftEnd: string | Date;
  isLate?: boolean;
  shiftId: number;
  assignmentId?: number;
  workerRemarks?: string;
  workerSuggestedTime?: number | null;
  facilityRemarks?: string;
  onRefresh?: () => void;
}

const WorkerAssigned: React.FC<WorkerAssignedProps> = ({
  name,
  id,
  avatarUrl,
  status,
  clockIn,
  clockOut,
  shiftStart,
  shiftEnd,
  isLate,
  assignmentId,
  workerRemarks,
  shiftId,
  workerSuggestedTime,
  facilityRemarks,
  onRefresh,
}) => {

  const [openActionModal, setOpenActionModal] = React.useState(false);

  const [verified, setVerified] = React.useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);

  useEffect(() => {
    if (status === 'facility_approval_pending') {
      setOpenActionModal(true);
      setVerified(false);
      setFeedbackSubmitted(false);
    }
  }, [status]);

  useEffect(() => {
    if (verified && feedbackSubmitted) {
      setTimeout(() => {
        setOpenActionModal(false);
        setVerified(false);
        setFeedbackSubmitted(false);
        if (onRefresh) onRefresh();
      }, 1000);
    }
  }, [verified, feedbackSubmitted, onRefresh]);

  const handleVerifySuccess = (action: 'accept' | 'reject') => {
    setVerified(true);
    if (action === 'reject') {
      setFeedbackSubmitted(true); // Skip feedback if rejected
    }
  };

  const handleFeedbackSuccess = () => {
    setFeedbackSubmitted(true);
  };

  const handleOpenActionModal = () => {
    if (status === 'facility_approval_pending') {
      setOpenActionModal(true);
      setVerified(false);
      setFeedbackSubmitted(false);
    }
  };

  const calculateWorkerProgress = (
    shiftStart: string | Date,
    shiftEnd: string | Date,
    clockIn: string | Date | null,
    clockOut?: string | Date | null
  ): number => {
    if (!clockIn) return 0;

    const start = dayjs(shiftStart);
    const end = dayjs(shiftEnd);
    const inTime = dayjs(clockIn);

    const totalShiftDuration = end.diff(start);

    if (totalShiftDuration <= 0) return 0;

    const effectiveEndTime = clockOut ? dayjs(clockOut) : dayjs();
    const timeWorked = effectiveEndTime.diff(inTime);
    const percentage = (timeWorked / totalShiftDuration) * 100;

    return Math.min(Math.max(percentage, 0), 100);
  };

  const getWorkerDuration = (
    clockIn: string | Date | null,
    clockOut?: string | Date | null
  ): string => {
    if (!clockIn) return '0h 0m';

    const start = dayjs(clockIn);
    const end = clockOut ? dayjs(clockOut) : dayjs();
    const totalMinutes = end.diff(start, 'minute');
    if (totalMinutes <= 0) return '0h 0m';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const getShiftCountdown = () => {
    const now = dayjs();
    const start = dayjs(shiftStart);
    const diffInMinutes = start.diff(now, 'minute');

    if (diffInMinutes <= 0) return null;

    const days = Math.floor(diffInMinutes / (24 * 60));
    const hours = Math.floor((diffInMinutes % (24 * 60)) / 60);

    let result = '';
    if (days > 0) result += `${days} day${days !== 1 ? 's' : ''} `;
    if (hours > 0) result += `${hours} hour${hours !== 1 ? 's' : ''}`;

    return result.trim() || 'Less than 1 hour';
  };

  const isWorkerCancelled = status === 'worker_cancelled';
  const isFacilityRejected = status === 'facility_reject';
  const countdownString = getShiftCountdown();
  const showCountdown = !isWorkerCancelled && !clockIn && countdownString !== null;

  return (
    <Container onClick={handleOpenActionModal} sx={{ cursor: status === 'facility_approval_pending' ? 'pointer' : 'default' }}>
      <SectionTitle>Worker Assigned</SectionTitle>

      {/* Worker Info & Status */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box display="flex" gap={2}>
          <SecureAvatar
            src={avatarUrl}
            alt={name}
            sx={{ width: 48, height: 48 }}
          />
          <Box>
            <WorkerName>{name}</WorkerName>
            <WorkerId>ID : {id}</WorkerId>
          </Box>
        </Box>
        <StatusChip
          label={status === 'worker_cancelled' ? 'Worker Cancelled' : status === 'facility_reject' ? 'Facility Rejected' : (isLate) ? 'Late' : "On Time"}
          $isError={status === 'worker_cancelled' || status === 'facility_reject' || (isLate || false)}
        />
      </Box>

      {((isWorkerCancelled && workerRemarks) || (isFacilityRejected && facilityRemarks)) && (
        <CountdownBox sx={{ backgroundColor: '#FFEBEE', '& p': { color: '#D32F2F' }, mb: 2 }}>
          <img src={WarningIcon} alt='warning-icon' />
          <CountdownText sx={{ color: '#D32F2F' }}>
            Remarks: {isWorkerCancelled ? workerRemarks : facilityRemarks}
          </CountdownText>
        </CountdownBox>
      )}

      {showCountdown ? (
        <CountdownBox>
          {/* <StyledWarningIcon /> */}
          <img src={WarningIcon} alt='warning-icon' />
          <CountdownText>
            Shift Starts in {countdownString}
          </CountdownText>
        </CountdownBox>
      ) : (
        (!isWorkerCancelled || !workerRemarks) && (
          <>
            {/* Progress Bar */}
            <Box mb={3}>
              <DurationText>{getWorkerDuration(clockIn, clockOut)}</DurationText>
              <StyledLinearProgress
                variant="determinate"
                value={calculateWorkerProgress(shiftStart, shiftEnd, clockIn, clockOut)}
              />
            </Box>

            {/* Clock In / Clock Out */}
            <Grid container spacing={2}>
              <Grid size={6}>
                <Box>
                  <TimeLabel>Clock In</TimeLabel>
                  <TimeValue>{clockIn ? dayjs(clockIn).format('HH:mm') : "-"}</TimeValue>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <TimeLabel>Clock Out</TimeLabel>
                  <TimeValue>{clockOut ? dayjs(clockOut).format('HH:mm') : "-"}</TimeValue>
                </Box>
              </Grid>
            </Grid>
          </>
        )
      )}


      <ActionModal
        // open={(status === 'facility_approval_pending' ? true : false)}
        open={openActionModal}
        height={"85vh"}
        onClose={() => { setOpenActionModal(false) }}
      >
        <VerifyShift
          clockIn={clockIn}
          clockOut={clockOut}
          workerRemarks={workerRemarks}
          workerSuggestedTime={workerSuggestedTime}
          assignmentId={assignmentId!}
          shiftId={shiftId}
          shiftStart={dayjs(shiftStart).toISOString()}
          shiftEnd={dayjs(shiftEnd).toISOString()}
          onSuccess={handleVerifySuccess}
        />
        <PerformanceFeedback
          workerId={id as number}
          shiftId={shiftId}
          onSuccess={handleFeedbackSuccess}
          onCancel={() => setOpenActionModal(false)}
        />
      </ActionModal>
    </Container>
  );
};

export default WorkerAssigned;